/**
 * IN-APP BROWSER DETECTION, and the way out of one (2026-09-25, the Revent
 * mobile first touch: docs/internal/architecture/revent-mobile-first-touch.md,
 * section 2.4 "The account is the barrier, and there is exactly one door").
 *
 * A link opened from inside LinkedIn, Instagram, Facebook, Messenger, X,
 * TikTok, Snapchat, WeChat, LINE and their kind does not open the phone's
 * browser. It opens an embedded web view owned by that app, with its own
 * cookie jar and usually no address bar. Google refuses OAuth there with
 * `403 disallowed_useragent` and there is no setting that turns that off, so
 * the portal's primary sign-in is simply dead on the exact surface a shared
 * link or a scanned code most often lands on. Nothing stored in that jar
 * follows the person out either, which is why the design puts the escape on
 * the FIRST screen rather than next to the button that fails.
 *
 * This module answers two questions from the user agent string alone, so a
 * table of real strings can pin them with no browser in the loop:
 *
 *   1. `detectInAppBrowser(ua)`: is this an embedded web view, whose app if
 *      the token is known, and which operating system.
 *   2. `inAppEscapeHref(href, os)`: a URL that hands THIS page to the real
 *      browser where the platform offers a way. Android: an `intent://` URL
 *      pinned to Chrome, with the page itself as the fallback. iOS: the
 *      `x-safari-https://` scheme Safari registers. Both carry the page's
 *      `#fragment`, because the fragment can be a live arrival code
 *      (lib/auth/arrival-fragment.ts) and dropping it would throw away the
 *      reason the person came.
 *
 * DETECTION IS TWO LAYERS. Named app tokens first: the strings those apps
 * append to the stock engine UA. Then the engine's own web view markers:
 * Chromium's WebView adds `; wv)` to the platform section (Android 5 and
 * later), and a WKWebView on iOS carries `Mobile/` but never `Safari/`
 * (Safari, Chrome, Firefox, Edge and DuckDuckGo on iOS all keep the
 * `Safari/` token). A home-screen web app on iOS has the same shape as a
 * WKWebView, so callers pass `standalone` and the generic iOS rule stands
 * down: the person chose that surface, and it is not another app's browser.
 *
 * WHAT IT WILL NOT DO. A false positive costs one dismissible sheet; a false
 * negative costs the whole sign-in. Even so the generic rules fire only on
 * the engine's own markers. Chrome Custom Tabs, Trusted Web Activities and
 * SFSafariViewController ARE the real browser and carry its UA, so they are
 * correctly not flagged. The `Version/4.0 ... Chrome/` shape of pre-Lollipop
 * web views is deliberately not a rule: UC Browser and other real browsers
 * carry it too, and those devices are gone.
 *
 * Plain module, no DOM access: the caller hands in `navigator.userAgent`.
 */

export type InAppBrowserApp =
  | 'facebook'
  | 'messenger'
  | 'instagram'
  | 'threads'
  | 'linkedin'
  | 'x'
  | 'tiktok'
  | 'snapchat'
  | 'wechat'
  | 'line'
  | 'pinterest';

export type InAppBrowserOs = 'ios' | 'android' | 'other';

export interface InAppBrowserDetection {
  /** True inside an embedded web view, named or not. */
  inApp: boolean;
  /**
   * The app whose browser this is, when its token is known. Null for a bare
   * web view (some other app) and for a real browser.
   */
  app: InAppBrowserApp | null;
  os: InAppBrowserOs;
}

/**
 * Brand names for the copy. Not translated: they are marks, not words. LINE
 * is spelled the way the company spells it.
 */
export const IN_APP_BROWSER_NAMES: Readonly<Record<InAppBrowserApp, string>> = {
  facebook: 'Facebook',
  messenger: 'Messenger',
  instagram: 'Instagram',
  threads: 'Threads',
  linkedin: 'LinkedIn',
  x: 'X',
  tiktok: 'TikTok',
  snapchat: 'Snapchat',
  wechat: 'WeChat',
  line: 'LINE',
  pinterest: 'Pinterest',
};

/**
 * Named tokens, most specific first. A Messenger UA also carries the
 * Facebook `FBAN/` and `FBAV/` markers, so Messenger must be tried before
 * Facebook. Word boundaries are placed only where the real strings have
 * one: Snapchat's Android UA runs the version straight into the name
 * (`Snapchat12.51.0.42`) and TikTok's iOS token is `musical_ly_34.0.0`.
 */
const APP_TOKENS: ReadonlyArray<readonly [InAppBrowserApp, RegExp]> = [
  ['messenger', /\bFBAN\/Messenger|Orca-Android/],
  ['facebook', /\bFBAN\/|\bFBAV\/|\bFB_IAB\/|\bFBIOS\b|\bFB4A\b/],
  ['instagram', /\bInstagram\b/],
  // Threads still announces itself under its internal name.
  ['threads', /\bBarcelona\b/],
  ['linkedin', /\bLinkedInApp\b/],
  ['x', /Twitter for iPhone|Twitter for iPad|\bTwitterAndroid\b/],
  ['tiktok', /\bBytedanceWebview\b|\bmusical_ly|\bTikTok\b|\btrill_\d/],
  ['snapchat', /\bSnapchat/],
  ['wechat', /\bMicroMessenger\b/],
  // Case-sensitive on purpose: `Line/14.6.0/IAB`, never the word "outline".
  ['line', /\bLine\/\d/],
  ['pinterest', /\bPinterest\b/],
];

const IOS_DEVICE_RE = /\b(?:iPhone|iPad|iPod)\b/;
const ANDROID_RE = /\bAndroid\b/;
/** Chromium WebView's own marker: `Build/...; wv)` in the platform section. */
const ANDROID_WEBVIEW_RE = /;\s*wv\)/;
/** The iOS engine's build token, present in every WKWebView and every iOS browser. */
const IOS_MOBILE_RE = /\bMobile\/[0-9A-Za-z]+/;
/** Every real iOS browser keeps this; a bare WKWebView never has it. */
const SAFARI_TOKEN_RE = /\bSafari\//;

/**
 * Is this user agent an embedded web view, and whose? Pure: hand in
 * `navigator.userAgent`. `standalone` is the caller's reading of
 * `display-mode: standalone` or `navigator.standalone`, and turns off the
 * generic iOS rule for a home-screen web app.
 */
export function detectInAppBrowser(
  userAgent: unknown,
  opts: { standalone?: boolean } = {},
): InAppBrowserDetection {
  const ua = typeof userAgent === 'string' ? userAgent : '';
  const os: InAppBrowserOs = IOS_DEVICE_RE.test(ua)
    ? 'ios'
    : ANDROID_RE.test(ua)
      ? 'android'
      : 'other';
  for (const [app, re] of APP_TOKENS) {
    if (re.test(ua)) return { inApp: true, app, os };
  }
  if (os === 'android' && ANDROID_WEBVIEW_RE.test(ua)) {
    return { inApp: true, app: null, os };
  }
  if (os === 'ios' && !opts.standalone && IOS_MOBILE_RE.test(ua) && !SAFARI_TOKEN_RE.test(ua)) {
    return { inApp: true, app: null, os };
  }
  return { inApp: false, app: null, os };
}

/** Chrome ships on practically every Android phone; the intent is pinned to it. */
const ANDROID_CHROME_PACKAGE = 'com.android.chrome';

/** The browser the escape opens, for the copy. Null where there is no direct escape. */
export function inAppEscapeBrowser(os: InAppBrowserOs): 'Chrome' | 'Safari' | null {
  if (os === 'android') return 'Chrome';
  if (os === 'ios') return 'Safari';
  return null;
}

/**
 * A URL that opens `href` in the real browser, or null where the platform
 * offers none (then the copy box is the escape). Only `http(s)` pages are
 * ever handed over, and the page's own fragment travels with it.
 *
 * ANDROID. `Intent.parseUri` splits the string at the LAST `#`, so the
 * page's fragment rides inside the data part and `#Intent;...;end` stays the
 * delimiter; the data becomes `https://host/path?query#fragment` once the
 * `scheme=` extra replaces `intent`. The fallback is percent-encoded whole:
 * a raw `;` would end the extra early and a raw `#` would end the intent.
 *
 * iOS. `x-safari-https://host/path?query#fragment` is the same URL with the
 * scheme swapped; Safari opens the `https` page it names.
 */
export function inAppEscapeHref(href: unknown, os: InAppBrowserOs): string | null {
  if (typeof href !== 'string' || !href) return null;
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  const scheme = url.protocol.slice(0, -1);
  const rest = `${url.host}${url.pathname}${url.search}${url.hash}`;
  if (os === 'android') {
    return (
      `intent://${rest}#Intent;scheme=${scheme};package=${ANDROID_CHROME_PACKAGE};` +
      `S.browser_fallback_url=${encodeURIComponent(href)};end`
    );
  }
  if (os === 'ios') return `x-safari-${scheme}://${rest}`;
  return null;
}
